'use client';

import type { FC, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/cn';

interface CellProps extends HTMLAttributes<HTMLDivElement> {
  /** Leading content — usually an SF Symbol-style icon (15pt centered, 28pt wide slot). */
  before?: ReactNode;
  /** Trailing content. Pass the string "›" or set `chevron` to render an iOS chevron SVG. */
  after?: ReactNode;
  /** Secondary line under the title. */
  subtitle?: ReactNode;
  /** Render an explicit iOS chevron in the trailing slot (in addition to `after`). */
  chevron?: boolean;
  /** Mark as the visually last row — hides the bottom hairline. */
  isLast?: boolean;
  /** Tint the title text. */
  tone?: 'default' | 'destructive' | 'disabled' | 'accent';
  /** When `tone === 'accent'`. */
  accentColor?: string;
  /** @deprecated kept for back-compat; no longer changes layout in iOS 26 Menu spec. */
  multiline?: boolean;
}

/**
 * iOS 26 Menu / Inset Grouped row.
 *
 * Spec source: Figma `iOS and iPadOS 26 (Community)` node 5511:27555.
 *   - pl-[6px] pr-[8px] gap-[6px]
 *   - leading icon 28pt wide, text-[15px] leading-[20px]
 *   - label SF Pro Regular 15/18 tracking 0.2px
 *   - subtitle SF Pro Regular 13/16 Vibrant Secondary
 *   - chevron SF Pro Bold 15 (here SVG, viewBox 0 0 7 12, stroke 1.6)
 *   - separator h-px inset by 42px from left, hidden on last child
 */
export const Cell: FC<CellProps> = ({
  children,
  before,
  after,
  subtitle,
  chevron,
  isLast,
  tone = 'default',
  accentColor,
  className,
  onClick,
  ...props
}) => {
  const titleColor =
    tone === 'destructive' ? 'text-accent-red'
    : tone === 'disabled' ? 'text-label-tertiary'
    : tone === 'accent' ? ''
    : 'text-label-primary';

  const titleStyle = tone === 'accent' && accentColor ? { color: accentColor } : undefined;

  // Treat legacy `after="›"` as a chevron request — auto-substitute the SVG.
  const afterIsChevronString = typeof after === 'string' && after === '›';
  const showChevron = chevron || afterIsChevronString;
  const trailingContent = afterIsChevronString ? undefined : after;

  const interactive = !!onClick && tone !== 'disabled';

  // Separator left inset: starts at the leading edge of the text content.
  // With a `before` icon (28pt slot + gap + outer padding) = 42px.
  // Without leading content = 14px (outer padding only).
  const sepLeft = before !== undefined ? 'after:left-[42px]' : 'after:left-[14px]';

  return (
    <div
      onClick={interactive ? onClick : undefined}
      className={cn(
        'flex items-center w-full pl-[6px] pr-[8px] gap-[6px] relative',
        subtitle ? 'min-h-[58px]' : 'min-h-[44px]',
        !isLast && cn('after:content-[""] after:absolute after:right-[8px] after:bottom-0 after:h-px after:bg-white/[0.07]', sepLeft),
        isLast === undefined && 'last:after:hidden',
        interactive ? 'cursor-pointer' : 'cursor-default',
        className,
      )}
      {...props}
    >
      <div className="flex gap-[8px] items-center flex-1 min-w-0">
        {before !== undefined && (
          <span
            className={cn(
              'w-[28px] text-[15px] leading-[20px] text-center shrink-0 flex items-center justify-center',
              tone === 'disabled' && 'text-label-tertiary',
            )}
          >
            {before}
          </span>
        )}
        <div className="flex flex-col gap-[2px] items-start justify-center py-[11px] pl-[4px] flex-1 min-w-0">
          <span
            className={cn('text-[15px] leading-[18px] tracking-[0.2px] truncate w-full', titleColor)}
            style={titleStyle}
          >
            {children}
          </span>
          {subtitle !== undefined && (
            <span
              className={cn(
                'text-[13px] leading-[16px] truncate w-full',
                tone === 'disabled' ? 'text-label-tertiary' : 'text-label-secondary',
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {trailingContent !== undefined && (
        <span className="shrink-0 text-[15px] leading-[18px] tracking-[0.2px] text-right text-label-secondary truncate max-w-[60%]">
          {trailingContent}
        </span>
      )}
      {showChevron && (
        <span className="w-[14px] h-[20px] shrink-0 flex items-center justify-center text-label-tertiary">
          <Chevron />
        </span>
      )}
    </div>
  );
};

const Chevron: FC = () => (
  <svg aria-hidden viewBox="0 0 7 12" width="7" height="12" className="shrink-0">
    <path
      d="M1 1 L6 6 L1 11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
